import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { track, identifyUser, EVENTS } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* Sources that behave as waitlist signups (not just newsletter). */
const WAITLIST_SOURCES = new Set([
  "inner_circle",
  "resources_videos",
  "resources_podcast",
  "resources_articles",
  "resources_community",
]);

/**
 * Reusable email signup used on multiple pages.
 * Tag subscriber `source` with page context for later segmentation.
 */
export default function EmailSignup({
  source = "wishing_wellness",
  headline = "Join the Wishing Wellness Community",
  subtext = "Receive weekly encouragement, wellness tips, free resources, and practical tools to help you become your healthiest self.",
  overline = "Start Free",
  buttonLabel = "Join Free",
  compact = false,
  testIdPrefix = "join",
}) {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !email.trim()) {
      toast.error("Please enter your first name and email.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/subscribe`, {
        first_name: firstName.trim(),
        email: email.trim(),
        source,
      });
      identifyUser(email, { first_name: firstName.trim(), source });
      const isWaitlist = WAITLIST_SOURCES.has(source);
      track(isWaitlist ? EVENTS.WAITLIST_JOINED : EVENTS.NEWSLETTER_SUBSCRIBED, {
        source,
      });
      setSubmitted(true);
      toast.success(res.data?.message || "You're in. Check your inbox.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your info and try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="rounded-3xl bg-white border border-[#EDE9E1] p-8 md:p-10 shadow-sm" data-testid={`${testIdPrefix}-success`}>
        <div className="flex items-center gap-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#8FAF8A]">
            <Check className="h-4 w-4 text-white" />
          </span>
          <div className="font-display font-bold text-lg text-[#333333]">
            You&apos;re in, {firstName}.
          </div>
        </div>
        <p className="mt-3 text-[#4A4A4A]">
          Your free 7-Day Discipline Challenge is on its way. If it&apos;s not in your inbox in a minute, check spam.
        </p>
        <a
          href={`${API}/download-challenge`}
          target="_blank"
          rel="noreferrer"
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#333333] hover:bg-black text-white h-11 px-6 text-sm font-semibold transition-all"
          data-testid={`${testIdPrefix}-download-link`}
        >
          Download the PDF now
          <ArrowRight className="h-4 w-4" />
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className={compact ? "space-y-3" : "space-y-4"} data-testid={`${testIdPrefix}-form`}>
      {overline && !compact && (
        <div className="text-xs font-bold tracking-[0.25em] uppercase text-[#8FAF8A]">{overline}</div>
      )}
      {headline && !compact && (
        <h3 className="font-display text-3xl md:text-4xl font-extrabold tracking-tight text-[#333333]">{headline}</h3>
      )}
      {subtext && !compact && (
        <p className="text-[#4A4A4A] text-base md:text-lg leading-relaxed max-w-lg">{subtext}</p>
      )}

      <div className={compact ? "flex flex-col sm:flex-row gap-3" : "space-y-3 pt-2"}>
        <Input
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="First name"
          className="h-13 rounded-2xl bg-white border-[#EDE9E1] px-5 text-base focus-visible:ring-2 focus-visible:ring-[#8FAF8A] focus-visible:ring-offset-0"
          autoComplete="given-name"
          data-testid={`${testIdPrefix}-input-first-name`}
        />
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email address"
          className="h-13 rounded-2xl bg-white border-[#EDE9E1] px-5 text-base focus-visible:ring-2 focus-visible:ring-[#8FAF8A] focus-visible:ring-offset-0"
          autoComplete="email"
          data-testid={`${testIdPrefix}-input-email`}
        />
        <Button
          type="submit"
          disabled={loading}
          className="h-13 rounded-full bg-[#8FAF8A] hover:bg-[#6E8F6A] text-white text-base font-semibold shadow-lg shadow-[#8FAF8A]/25 transition-all hover:-translate-y-0.5 disabled:opacity-70 whitespace-nowrap px-7"
          data-testid={`${testIdPrefix}-submit`}
        >
          {loading ? "Sending…" : buttonLabel}
          {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
        </Button>
      </div>
      <p className="text-sm text-[#8A8A8A] pt-1" data-testid={`${testIdPrefix}-microcopy`}>
        No spam. Only encouragement.
      </p>
    </form>
  );
}
